/**
 * Discriminated union of all messages that can flow on the WebView ↔ native
 * bridge. Each variant carries `type` (the discriminator) and `payload`. The
 * shape is intentionally simple to keep the JSON wire format stable.
 *
 * Bumps to BRIDGE_VERSION are breaking changes — older native clients won't
 * understand newer payload shapes (and vice-versa).
 *
 * NOTE: types are hand-written (not derived from TypeBox) so this module is
 * Hermes-safe (no `@sinclair/typebox` import). When/if a backend endpoint
 * needs a TypeBox schema for these messages, define it in a separate file.
 */
import { MOBILE_APP_URL_SCHEME } from './mobile_app/mobile_app.store';
import { stripLocalePrefix } from './translation_locale';

export const BRIDGE_VERSION = 1 as const;

// Seules les destinations RÉELLES du Tab.Navigator natif : le 5e slot « Menu »
// n'est pas navigable (tap preventDefault → overlay), il n'appartient pas au
// contrat. L'ex-tab « More » a été supprimé lors de la refonte overlay.
export const MOBILE_TAB_NAMES = ['Home', 'Agenda', 'Caisse', 'Clients'] as const;
export type MobileTabName = (typeof MOBILE_TAB_NAMES)[number];

/**
 * Racine web (path SANS préfixe de locale) de chaque tab natif. Source de
 * vérité unique consommée par le natif (paths des WebViews de tabs) et par
 * `resolveMobileTabForPath` (interception des clics côté web).
 */
export const MOBILE_TAB_ROOT_PATHS: Record<MobileTabName, string> = {
  Home: '/',
  Agenda: '/agenda',
  Caisse: '/sales',
  Clients: '/customers',
};

/**
 * `path` (query string et hash ignorés) est-il `prefix` lui-même ou une de ses
 * sous-pages ? Test sur une frontière de segment : `/salesX` ne matche pas `/sales`.
 * Source unique du rapprochement chemin ↔ racine (tabs, allowlist des liens, écrans
 * MoreWeb de la coque).
 */
export function isPathWithinPrefix(path: string, prefix: string): boolean {
  const pathname = path.split(/[?#]/, 1)[0];
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/**
 * Tab natif « propriétaire » d'un path web (path SANS préfixe de locale),
 * ou null si le path n'appartient au territoire d'aucun tab (ex. `/catalog`)
 * — auquel cas la navigation reste in-frame dans la WebView courante.
 *
 * `/` ne matche Home QUE sur égalité exacte (sinon tout le site basculerait
 * vers Home), et les préfixes se testent sur une frontière de segment
 * (`/salesX` ne doit pas matcher `/sales`). Query string et hash sont ignorés.
 */
export function resolveMobileTabForPath(path: string): MobileTabName | null {
  const pathname = path.split(/[?#]/, 1)[0];
  for (const tab of MOBILE_TAB_NAMES) {
    const root = MOBILE_TAB_ROOT_PATHS[tab];
    if (root === '/') {
      if (pathname === '/') return tab;
      continue;
    }
    if (isPathWithinPrefix(pathname, root)) return tab;
  }
  return null;
}

/**
 * Capacités natives que la coque installée peut déclarer dans
 * `window.__PROPELSIA_MOBILE__.capabilities`. Le web ne doit activer un chemin
 * natif (dictée micro, download via bridge, pick caméra) QUE si la capability
 * est déclarée — indispensable avec un `runtimeVersion` OTA fixe : un bundle
 * OTA récent ne doit jamais appeler un bridge absent des coques déjà
 * installées. Côté coque, une capability n'entre dans la liste déclarée
 * (`SHELL_CAPABILITIES`) que quand le natif correspondant est réellement câblé
 * et shippé dans une build store.
 */
export const MOBILE_CAPABILITIES = [
  'microphone',
  'file.download',
  'camera.pick',
  'navigation.switch_tab',
  // Écran Pulse natif (pur JS : handler + React Navigation, aucun module natif).
  'pulse.open',
  // Réception des liens de retour `propelsia://open` (pur JS : `appLinks.ts`). Le web ne
  // demande un retour dans l'app (`from_mobile_app`) qu'aux coques qui la déclarent.
  'app_link.open',
  // Scanner de QR natif (`scanner.scan` → `scanner.result`) : coupons au comptoir. Déclaré par
  // les coques dont le binaire embarque expo-camera ; l'appareil peut quand même répondre
  // `unavailable` (Android sans services Google).
  'scanner.scan',
  // Android : le bouton retour matériel ferme d'abord la couche web ouverte (modale, panneau, menu,
  // liste déroulante) au lieu de quitter la page (`navigation.layer_open` → `navigation.back`).
  // Pur JS (`AppWebView`). Sans elle, le web ne publie pas l'état de ses couches.
  'navigation.back',
  // Pastille du nombre de notifications non lues sur l'onglet Accueil (`app.unread_notifications`).
  // Pur JS (handler bridge + barre d'onglets).
  'notifications.badge',
] as const;
export type MobileCapability = (typeof MOBILE_CAPABILITIES)[number];

export type CameraSource = 'camera' | 'library';

/**
 * Persistent profile snapshot the native shell stores after login or account
 * switch. Used by native screens (e.g. the More tab) to display the active
 * professional without decoding the JWT.
 */
export interface MobileCurrentPro {
  professional_id: number;
  name: string;
  role: string;
  account_firstname?: string;
  account_lastname?: string;
}

/**
 * Statuts d'un refresh de session exécuté par le natif (miroir de
 * `RefreshSessionResult` côté mobile) : `ok` porte un token frais ; `invalid` /
 * `no_session` = session réellement morte (re-login requis) ; `network_error` =
 * panne transitoire — la session peut encore être valide, ne PAS déconnecter.
 */
export const REFRESH_RESULT_STATUSES = ['ok', 'invalid', 'network_error', 'no_session'] as const;
export type RefreshResultStatus = (typeof REFRESH_RESULT_STATUSES)[number];

/**
 * Issue d'un scan natif (`scanner.result`) : `ok` porte le texte lu ; `cancelled` = le pro a
 * fermé le scanner ; `unavailable` = pas de scanner sur cet appareil (Android sans services
 * Google) ; `permission_denied` = caméra refusée ; `error` = échec ou scan déjà en cours.
 */
export const SCANNER_RESULT_STATUSES = ['ok', 'cancelled', 'unavailable', 'permission_denied', 'error'] as const;
export type ScannerResultStatus = (typeof SCANNER_RESULT_STATUSES)[number];

export type BridgeMessage =
  | {
      type: 'auth.token_refreshed';
      payload: { access_token: string; expires_at?: string };
    }
  | { type: 'auth.session_expired'; payload: Record<string, never> }
  | {
      // web → natif : la WebView a pris un 401, le natif (seul détenteur du
      // refresh token, en SecureStore) doit échanger un access token frais.
      type: 'auth.refresh_request';
      payload: { request_id: string };
    }
  | {
      // natif → web : réponse à `auth.refresh_request` (corrélée par request_id).
      type: 'auth.refresh_result';
      payload: { request_id: string; status: RefreshResultStatus; token?: string };
    }
  | {
      // web → natif : signup abouti dans la WebView register — le natif persiste
      // la session (SecureStore) et bascule l'app en mode authentifié.
      type: 'auth.account_created';
      payload: {
        token: string;
        refresh_token: string;
        refresh_token_expires_at?: string;
        pro: MobileCurrentPro;
      };
    }
  | {
      // web → natif : le lien cliqué appartient au territoire d'un AUTRE tab —
      // la coque bascule de tab (WebViews préchargées : switch instantané).
      // `path` (relatif à la locale) n'est présent que pour un chemin profond
      // (ex. `/customers/123`) ; absent = simple bascule vers la racine du tab.
      type: 'navigation.switch_tab';
      payload: { tab: MobileTabName; path?: string };
    }
  | {
      // natif → web : demande de navigation SPA dans une WebView déjà chargée
      // (deep link sans remount — contrat figé dès maintenant, câblage Phase 3).
      type: 'navigation.navigate';
      payload: { request_id: string; path: string };
    }
  | {
      // web → natif : réponse à `navigation.navigate` (corrélée par request_id).
      // `ok: false` (ou timeout côté natif) → fallback remount de la WebView.
      type: 'navigation.navigate_result';
      payload: { request_id: string; ok: boolean };
    }
  | {
      // web → natif : fermer l'écran natif hôte (pop du stack). Émis par le
      // bouton retour web quand la WebView est à la racine d'un écran poussé
      // (`presentation: 'pushed'`) — il n'y a plus d'historique web à dépiler.
      type: 'navigation.close';
      payload: Record<string, never>;
    }
  | {
      // web → natif : la page a (ou n'a plus) une couche ouverte que le bouton retour Android doit
      // fermer — modale, panneau, menu, liste déroulante. Émis à chaque changement, derrière la
      // capability `navigation.back`. Un chargement de document remet l'état à « fermé ».
      type: 'navigation.layer_open';
      payload: { open: boolean };
    }
  | {
      // natif → web : bouton retour Android pressé pendant qu'une couche est ouverte. Le web ferme
      // la couche du dessus, comme la touche Échap sur ordinateur ; aucune réponse attendue.
      type: 'navigation.back';
      payload: Record<string, never>;
    }
  | {
      // natif → web : le pro a re-touché l'onglet déjà affiché alors que sa page est à la racine.
      // Le web remonte la page en haut. Une page antérieure le rejette (no-op).
      type: 'navigation.scroll_top';
      payload: Record<string, never>;
    }
  | {
      type: 'share.open';
      payload: { url: string; title?: string; message?: string };
    }
  | {
      type: 'camera.pick';
      payload: { request_id: string; source: CameraSource };
    }
  | {
      type: 'camera.result';
      payload: {
        request_id: string;
        cancelled: boolean;
        base64?: string;
        mime_type?: string;
        error?: string;
      };
    }
  | {
      type: 'biometric.prompt';
      payload: { request_id: string; reason: string };
    }
  | {
      type: 'biometric.result';
      payload: { request_id: string; success: boolean; error?: string };
    }
  | {
      type: 'app.account_switch';
      payload: { professional_id: number; token: string; pro: MobileCurrentPro };
    }
  | { type: 'app.logout'; payload: Record<string, never> }
  | {
      // natif → web : la page redevient visible SANS avoir été rechargée — son onglet est
      // refocalisé (bascule de tab, retour d'un écran poussé) ou l'app revient au premier plan.
      // Les WebViews de tab restent montées en permanence et chacune a son propre cache : sans
      // ce signal, une donnée modifiée depuis un autre onglet n'y apparaît jamais. Le web
      // rafraîchit alors ses données périmées ; aucune réponse attendue. Une page antérieure à
      // ce message le rejette à la validation (no-op), une coque antérieure ne l'émet pas.
      type: 'app.page_visible';
      payload: Record<string, never>;
    }
  | {
      // web → natif : nombre de notifications non lues, publié par la WebView de l'onglet Accueil à
      // chaque changement, derrière la capability `notifications.badge`. La coque l'affiche en
      // pastille sur l'onglet ; aucune réponse attendue.
      type: 'app.unread_notifications';
      payload: { count: number };
    }
  | {
      // web → natif : scanner un QR code (coupon au comptoir). Gardé côté web par la
      // capability `scanner.scan` : une coque antérieure droppe le message.
      type: 'scanner.scan';
      payload: { request_id: string };
    }
  | {
      // natif → web : réponse à `scanner.scan` (corrélée par request_id). Le texte lu n'est
      // jamais interprété par le natif : le web le passe à `parseVoucherScan`.
      type: 'scanner.result';
      payload: { request_id: string; status: ScannerResultStatus; data?: string };
    }
  | {
      // web → natif : ouvrir l'écran Pulse natif (poussé par-dessus les tabs).
      // `prefill` pré-remplit le composer (CTA ou puce de suggestion) sans
      // envoyer — c'est toujours le pro qui déclenche l'envoi. Gardé côté web
      // par la capability `pulse.open` : une coque antérieure droppe le message.
      type: 'pulse.open';
      payload: { prefill?: string };
    };

export type BridgeMessageType = BridgeMessage['type'];

const KNOWN_TYPES = new Set<string>([
  'auth.token_refreshed',
  'auth.session_expired',
  'auth.refresh_request',
  'auth.refresh_result',
  'auth.account_created',
  'navigation.switch_tab',
  'navigation.navigate',
  'navigation.navigate_result',
  'navigation.close',
  'navigation.layer_open',
  'navigation.back',
  'navigation.scroll_top',
  'share.open',
  'camera.pick',
  'camera.result',
  'biometric.prompt',
  'biometric.result',
  'app.account_switch',
  'app.logout',
  'app.page_visible',
  'app.unread_notifications',
  'pulse.open',
  'scanner.scan',
  'scanner.result',
]);

const TAB_NAMES = new Set<string>(MOBILE_TAB_NAMES);
const CAMERA_SOURCES = new Set<string>(['camera', 'library']);
const REFRESH_STATUSES = new Set<string>(REFRESH_RESULT_STATUSES);
const SCANNER_STATUSES = new Set<string>(SCANNER_RESULT_STATUSES);

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isStr(v: unknown): v is string {
  return typeof v === 'string';
}

function isOptStr(v: unknown): v is string | undefined {
  return v === undefined || typeof v === 'string';
}

function isValidPro(pro: unknown): boolean {
  return (
    isObject(pro) &&
    typeof pro.professional_id === 'number' &&
    isStr(pro.name) &&
    isStr(pro.role) &&
    isOptStr(pro.account_firstname) &&
    isOptStr(pro.account_lastname)
  );
}

function isPayloadValid(type: string, payload: Record<string, unknown>): boolean {
  switch (type) {
    case 'auth.token_refreshed':
      return isStr(payload.access_token) && isOptStr(payload.expires_at);
    case 'auth.session_expired':
    case 'navigation.close':
    case 'navigation.back':
    case 'navigation.scroll_top':
    case 'app.logout':
    case 'app.page_visible':
      return true;
    case 'navigation.layer_open':
      return typeof payload.open === 'boolean';
    case 'app.unread_notifications':
      return typeof payload.count === 'number' && Number.isInteger(payload.count) && payload.count >= 0;
    case 'auth.refresh_request':
      return isStr(payload.request_id);
    case 'auth.refresh_result':
      return (
        isStr(payload.request_id) &&
        isStr(payload.status) &&
        REFRESH_STATUSES.has(payload.status) &&
        isOptStr(payload.token)
      );
    case 'auth.account_created':
      return (
        isStr(payload.token) &&
        isStr(payload.refresh_token) &&
        isOptStr(payload.refresh_token_expires_at) &&
        isValidPro(payload.pro)
      );
    case 'navigation.switch_tab':
      return isStr(payload.tab) && TAB_NAMES.has(payload.tab) && isOptStr(payload.path);
    case 'navigation.navigate':
      return isStr(payload.request_id) && isStr(payload.path);
    case 'navigation.navigate_result':
      return isStr(payload.request_id) && typeof payload.ok === 'boolean';
    case 'share.open':
      return isStr(payload.url) && isOptStr(payload.title) && isOptStr(payload.message);
    case 'camera.pick':
      return isStr(payload.request_id) && isStr(payload.source) && CAMERA_SOURCES.has(payload.source);
    case 'camera.result':
      return (
        isStr(payload.request_id) &&
        typeof payload.cancelled === 'boolean' &&
        isOptStr(payload.base64) &&
        isOptStr(payload.mime_type) &&
        isOptStr(payload.error)
      );
    case 'biometric.prompt':
      return isStr(payload.request_id) && isStr(payload.reason);
    case 'biometric.result':
      return isStr(payload.request_id) && typeof payload.success === 'boolean' && isOptStr(payload.error);
    case 'app.account_switch':
      return typeof payload.professional_id === 'number' && isStr(payload.token) && isValidPro(payload.pro);
    case 'pulse.open':
      return isOptStr(payload.prefill);
    case 'scanner.scan':
      return isStr(payload.request_id);
    case 'scanner.result':
      return (
        isStr(payload.request_id) &&
        isStr(payload.status) &&
        SCANNER_STATUSES.has(payload.status) &&
        isOptStr(payload.data)
      );
    default:
      return false;
  }
}

export function validateBridgeMessage(input: unknown): BridgeMessage {
  if (!isObject(input)) throw new Error('Invalid BridgeMessage: not an object');
  const { type, payload } = input;
  if (!isStr(type) || !KNOWN_TYPES.has(type)) {
    throw new Error(`Invalid BridgeMessage: unknown type "${String(type)}"`);
  }
  if (!isObject(payload)) throw new Error(`Invalid BridgeMessage: payload not an object for "${type}"`);
  if (!isPayloadValid(type, payload)) {
    throw new Error(`Invalid BridgeMessage: malformed payload for "${type}"`);
  }
  return input as BridgeMessage;
}

export function isBridgeMessage(input: unknown): input is BridgeMessage {
  try {
    validateBridgeMessage(input);
    return true;
  } catch {
    return false;
  }
}

interface ReactNativeWebViewBridge {
  postMessage(payload: string): void;
}

/**
 * Send a typed message from a Next.js page (running inside the mobile WebView)
 * to the native shell. Validates the message shape before sending — a runtime
 * mismatch is much harder to debug than a thrown error.
 *
 * Returns true when the bridge accepted the message, false when running
 * outside a WebView (regular browser).
 */
export function sendToNative(message: BridgeMessage): boolean {
  validateBridgeMessage(message);

  if (typeof window === 'undefined') return false;

  const bridge = (window as unknown as { ReactNativeWebView?: ReactNativeWebViewBridge }).ReactNativeWebView;
  if (!bridge?.postMessage) return false;

  bridge.postMessage(JSON.stringify(message));
  return true;
}

/**
 * Canal natif → web : le natif injecte un script (`injectJavaScript`) qui
 * dispatche un CustomEvent sur `window` ; la page web écoute cet événement.
 * Le `detail` est une STRING JSON (double stringify côté natif) : le message
 * traverse le script injecté comme littéral inerte — aucune surface d'injection
 * — et la page le re-valide via `validateBridgeMessage` après parse.
 */
export const NATIVE_BRIDGE_EVENT_NAME = 'propelsia:native-bridge';

/** Construit le script à passer à `webViewRef.injectJavaScript` côté natif. */
export function buildNativeToWebScript(message: BridgeMessage): string {
  validateBridgeMessage(message);
  return `window.dispatchEvent(new CustomEvent(${JSON.stringify(NATIVE_BRIDGE_EVENT_NAME)}, { detail: ${JSON.stringify(
    JSON.stringify(message),
  )} })); true;`;
}

/**
 * Parse + valide le `detail` d'un CustomEvent bridge reçu côté web. Retourne
 * null sur message malformé (input non fiable — même contrat que le parse
 * natif de `dispatchBridgeMessage`).
 */
export function parseNativeBridgeEventDetail(detail: unknown): BridgeMessage | null {
  if (typeof detail !== 'string') return null;
  try {
    return validateBridgeMessage(JSON.parse(detail));
  } catch {
    return null;
  }
}

// ─── Lien de retour dans l'app : `propelsia://open?path=…&n=…` ───────────────

/**
 * Destinations EXACTES d'un lien `propelsia://open` — celles dont le RELAIS a besoin, et
 * rien d'autre (règles `07` §3 et `11` §4) : ni sous-page, ni query string, ni hash. Un
 * scheme custom n'appartient à personne : n'importe quelle app ou page web du téléphone
 * peut émettre `propelsia://`, et une sous-page ou un paramètre d'URL pourrait ouvrir une
 * modale pilotée par l'URL (`/establishment/place/12/delete`). Un lien hors liste est
 * ignoré (l'app passe au premier plan sans naviguer).
 * - `/establishment/gmb`, `/establishment/place` : retour de l'association Google
 *   Business Profile (page de départ, cf. `redirect_url` du state GMB) ;
 * - `/agenda/settings/sync` : retour de la synchro Google Agenda.
 * On n'ÉLARGIT pas cette liste pour rendre une branche atteignable.
 */
export const MOBILE_APP_LINK_ALLOWED_PREFIXES = [
  '/establishment/gmb',
  '/establishment/place',
  '/agenda/settings/sync',
] as const;
export type MobileAppLinkDestination = (typeof MOBILE_APP_LINK_ALLOWED_PREFIXES)[number];

const MOBILE_APP_LINK_PREFIX = `${MOBILE_APP_URL_SCHEME}://open`;

/**
 * Jeton à usage unique (`n`) : l'app ne traite qu'une fois un lien donné. Indispensable
 * sur Android, où une tâche relancée après la mort du process reprend son intent racine
 * d'origine — `Linking.getInitialURL` renverrait alors à chaque relance le lien qui a
 * ouvert l'app. Les liens sans jeton restent acceptés (simplement non dédoublonnés).
 */
const MOBILE_APP_LINK_NONCE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** Lien `propelsia://open` décodé. `nonce` vaut null si le lien n'en porte pas (ou un invalide). */
export interface MobileAppLink {
  path: MobileAppLinkDestination;
  nonce: string | null;
}

const isMobileAppLinkDestination = (path: string): path is MobileAppLinkDestination =>
  (MOBILE_APP_LINK_ALLOWED_PREFIXES as readonly string[]).includes(path);

/**
 * Destination autorisée qui porte `path` — la page elle-même ou une de ses sous-pages,
 * locale, query string et hash ignorés — ou null. Côté web : ramène un retour sur la page
 * d'atterrissage exacte (`/fr-FR/establishment/place/12/edit?x=1` → `/establishment/place`),
 * seule forme que l'app accepte.
 */
export function resolveMobileAppLinkDestination(path: string): MobileAppLinkDestination | null {
  const pathname = stripLocalePrefix(path.split(/[?#]/, 1)[0]);
  return (
    MOBILE_APP_LINK_ALLOWED_PREFIXES.find((destination): boolean => isPathWithinPrefix(pathname, destination)) ?? null
  );
}

// Unicité suffisante pour distinguer deux retours d'un même appareil : ce jeton ne
// protège rien (l'émetteur le choisit), il sert seulement à ne pas rejouer un lien.
const generateMobileAppLinkNonce = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/**
 * `/establishment/gmb` → `propelsia://open?path=%2Festablishment%2Fgmb&n=<jeton>`.
 * Le lien ne porte qu'une destination autorisée : jamais de `code`, de token ni
 * d'identifiant (règle `11` §3). `nonce` n'est à fournir que pour partager un même jeton
 * entre plusieurs tentatives (ouverture automatique + bouton) ou pour des tests.
 */
export function buildMobileAppLink(
  destination: MobileAppLinkDestination,
  nonce: string = generateMobileAppLinkNonce(),
): string {
  return `${MOBILE_APP_LINK_PREFIX}?path=${encodeURIComponent(destination)}&n=${encodeURIComponent(nonce)}`;
}

/**
 * Destination et jeton d'un lien `propelsia://open`, ou null si l'URL n'en est pas un ou
 * si le chemin n'est pas EXACTEMENT une destination autorisée (préfixe de locale toléré).
 * La comparaison exacte écarte d'office sous-pages, query string, hash, segments `..` et
 * encodages résiduels (`%2e%2e`). Toute autre URL reçue par l'app (lien du Dev Client
 * Expo…) retombe sur null et doit être ignorée par l'appelant.
 *
 * Parsing manuel : le polyfill `URL` de React Native ne gère ni `searchParams` ni les
 * schemes custom de façon fiable.
 */
export function parseMobileAppLink(url: string): MobileAppLink | null {
  // Scheme et hôte sont insensibles à la casse (RFC 3986), le chemin non.
  if (!url.toLowerCase().startsWith(MOBILE_APP_LINK_PREFIX)) return null;
  let rest = url.slice(MOBILE_APP_LINK_PREFIX.length);
  if (rest.startsWith('/')) rest = rest.slice(1);
  if (!rest.startsWith('?')) return null;

  const params = rest.slice(1).split('#', 1)[0].split('&');
  const readParam = (name: string): string | undefined =>
    params.find((param): boolean => param.startsWith(`${name}=`))?.slice(name.length + 1);
  const rawPath = readParam('path');
  if (!rawPath) return null;

  let destination: string;
  try {
    destination = stripLocalePrefix(decodeURIComponent(rawPath));
  } catch {
    return null;
  }
  if (!isMobileAppLinkDestination(destination)) return null;
  const rawNonce = readParam('n');
  return {
    path: destination,
    // Caractères non réservés uniquement : la valeur brute est déjà la valeur décodée.
    nonce: rawNonce && MOBILE_APP_LINK_NONCE_PATTERN.test(rawNonce) ? rawNonce : null,
  };
}
